import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTransferPage } from "@/lib/api";
import type { Transfer, TransferApproval, TransferInitiator, TransferInitiatorKind, TransferProvider } from "@/types";
import { transferStatusLabel, transferStatusState, transferStatusVariant } from "@/features/transfers/transfer-state";

export interface UseTransfersOptions {
  /** Narrows the read to one source wallet on the server (`wallet_id`). */
  walletId?: string;
  /** Server-side asset filter; a comma list is allowed (`asset`). */
  asset?: string;
  /** Server-side network filter; a comma list is allowed (`network`). */
  network?: string;
  /** Server-side composite-status filter (`status`). */
  status?: string;
  /** Server-side text search, 3–128 characters; anything shorter is not sent (`q`). */
  q?: string;
  /** Half-open RFC 3339 instant; only transfers created at or after it (`created_after`). */
  createdAfter?: string;
  /** Half-open RFC 3339 instant; only transfers created before it (`created_before`). */
  createdBefore?: string;
  pageSize?: number;
}

interface UseTransfersResult {
  transfers: Transfer[];
  loading: boolean;
  error: string | null;
  source: "api";
  refresh: () => void;
  /** The gateway has more transfers past the ones loaded (it returned a next_page_token). */
  hasMore: boolean;
  loadingMore: boolean;
  /** A failed "load more" read; the rows already loaded stay on screen. */
  loadMoreError: string | null;
  loadMore: () => void;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : value === null || value === undefined ? "" : String(value);
}

/** A value the gateway did not send stays null — an unknown, distinct from the empty string. */
function nullableText(value: unknown): string | null {
  const s = text(value);
  return s === "" ? null : s;
}

function nullableCount(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

const INITIATOR_KINDS = new Set<TransferInitiatorKind>(["human", "api_key", "service"]);

function mapInitiator(raw: unknown): TransferInitiator | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const principalId = text(r.principalId ?? r.principal_id);
  if (!principalId) return null;
  const kindRaw = text(r.kind);
  return {
    principalId,
    displayName: nullableText(r.displayName ?? r.display_name),
    kind: INITIATOR_KINDS.has(kindRaw as TransferInitiatorKind) ? (kindRaw as TransferInitiatorKind) : null,
  };
}

function mapProvider(raw: unknown): TransferProvider | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = text(r.id ?? r.providerId ?? r.provider_id);
  const displayName = text(r.displayName ?? r.display_name ?? r.name);
  return id || displayName ? { id, displayName } : null;
}

function mapApproval(raw: unknown): TransferApproval | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const taskId = nullableText(r.taskId ?? r.task_id);
  const required = nullableCount(r.required);
  const approved = nullableCount(r.approved);
  return taskId === null && required === null && approved === null ? null : { taskId, required, approved };
}

/**
 * The gateway projects each transfer as a `TransferView` (see
 * `alloy-api-gateway-service/.../transfer/TransferDetailResponse.java`):
 * `{ id, status, sourceWallet{id,name}, destination{address,network},
 *   amount, asset, timeline, degraded, failureReason, failureService,
 *   createdAt, updatedAt, transactionHash, initiator, provider, approval }`. The projection only
 * models outbound transfers (source wallet → destination address), so direction is always `send`.
 * ALL-5802: `initiator`/`provider`/`approval` are nullable and only as trustworthy as the
 * projection — a missing one maps to null so the UI can say "unknown" rather than guess.
 */
export function mapApiTransfer(raw: Record<string, unknown>): Transfer {
  const id = String(raw.id ?? raw.transferId ?? "");
  const sourceWallet = (raw.sourceWallet ?? {}) as Record<string, unknown>;
  const destination = (raw.destination ?? {}) as Record<string, unknown>;
  const source = String(sourceWallet.name ?? sourceWallet.id ?? "");
  const dest = String(destination.address ?? "");
  const route = [source, dest].filter(Boolean).join(" → ");
  const asset = String(raw.asset ?? "");
  const amount = String(raw.amount ?? "0");
  // The recorded projection carries no valuation field, so this resolves to null on today's
  // gateway. Read the field when the backend starts sending one instead of inventing a price.
  const amountUsdRaw = raw.amountUsd ?? raw.amount_usd;
  const statusRaw = String(raw.status ?? "pending");
  const created = String(raw.createdAt ?? "");
  const wallet = String(sourceWallet.id ?? "");

  return {
    id,
    direction: "send",
    route,
    asset,
    amount,
    amountUsd: amountUsdRaw === null || amountUsdRaw === undefined ? null : String(amountUsdRaw),
    status: statusRaw as Transfer["status"],
    statusLabel: transferStatusLabel(statusRaw),
    statusVariant: transferStatusVariant(statusRaw),
    created,
    creator: "",
    wallet,
    state: transferStatusState(statusRaw),
    sourceName: text(sourceWallet.name),
    destination: dest,
    network: text(destination.network),
    updated: text(raw.updatedAt),
    txHash: text(raw.transactionHash),
    initiator: mapInitiator(raw.initiator),
    provider: mapProvider(raw.provider),
    approval: mapApproval(raw.approval),
  };
}

function mapRows(rows: Record<string, unknown>[]): Transfer[] | null {
  // A row the mapper cannot read at all (null) makes the whole page an error rather than a
  // partial list; any other partial row degrades to blanks, never to invented values.
  try {
    return rows.map(mapApiTransfer);
  } catch {
    return null;
  }
}

export function useTransfers(options: UseTransfersOptions = {}): UseTransfersResult {
  const { walletId, asset, network, status, q, createdAfter, createdBefore, pageSize } = options;
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // Bumped on every first-page load so a slow response for an older query is dropped.
  const generation = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    setLoadMoreError(null);
    try {
      // No page token: a filter change always restarts paging, because the
      // gateway binds a token to the filter set it was issued for.
      const result = await fetchTransferPage({ walletId, asset, network, status, q, createdAfter, createdBefore, pageSize });
      if (gen !== generation.current) return;
      const rows = result.reasonCode ? null : mapRows(result.transfers);
      if (result.reasonCode || rows === null) {
        setTransfers([]);
        setNextToken(null);
        setError(result.reasonCode ?? "Malformed transfer list");
      } else {
        setTransfers(rows);
        setNextToken(result.nextPageToken);
      }
    } catch (e) {
      if (gen !== generation.current) return;
      setTransfers([]);
      setNextToken(null);
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, [walletId, asset, network, status, q, createdAfter, createdBefore, pageSize]);

  const loadMore = useCallback(async () => {
    if (!nextToken || loadingMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await fetchTransferPage({ walletId, asset, network, status, q, createdAfter, createdBefore, pageSize, pageToken: nextToken });
      if (gen !== generation.current) return;
      // The token was issued for a different filter set (e.g. this list was
      // re-filtered while a stale token was still in hand): restart paging from
      // the first page of the current filters instead of showing the 400.
      if (result.reasonCode === "PAGE_TOKEN_MISMATCH") {
        setLoadingMore(false);
        void load();
        return;
      }
      const rows = result.reasonCode ? null : mapRows(result.transfers);
      if (result.reasonCode || rows === null) {
        setLoadMoreError(result.reasonCode ?? "Malformed transfer list");
      } else {
        setTransfers((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          return [...prev, ...rows.filter((t) => !seen.has(t.id))];
        });
        setNextToken(result.nextPageToken);
      }
    } catch (e) {
      if (gen !== generation.current) return;
      setLoadMoreError(e instanceof Error ? e.message : "Network error");
    } finally {
      if (gen === generation.current) setLoadingMore(false);
    }
  }, [nextToken, loadingMore, walletId, asset, network, status, q, createdAfter, createdBefore, pageSize, load]);

  useEffect(() => { void load(); }, [load]);

  const refresh = useCallback(() => { void load(); }, [load]);
  const loadMoreAction = useCallback(() => { void loadMore(); }, [loadMore]);

  return {
    transfers,
    loading,
    error,
    source: "api",
    refresh,
    hasMore: nextToken !== null,
    loadingMore,
    loadMoreError,
    loadMore: loadMoreAction,
  };
}
